import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from "rxjs/operators";
import {AuthService} from "./auth.service";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający dostępem do modułu Produktów Partnerskich.
 * Weryfikuje po stronie serwera API, czy zalogowany użytkownik posiada uprawnienia
 * systemowe (RBAC) do przeglądania i zarządzania katalogiem produktów partnerów.
 */
@Injectable({
  providedIn: 'root'
})
export class PartnerProductAccessGuard implements CanActivate, CanLoad {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Weryfikuje uprawnienia przed fizyczną aktywacją i wyrenderowaniem widoku
   * katalogu produktów partnerskich. Przechwytuje błędy autoryzacji sesji (HTTP 401),
   * wymuszając bezpieczne wylogowanie użytkownika. W przypadku braku uprawnień
   * do samego modułu, przekazuje obsługę błędu do warstwy CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub drzewo routingu (UrlTree).
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/partner-products-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - partner-products - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) paczki JavaScript
   * modułu produktów partnerskich z serwera. Stanowi pierwszą linię obrony chroniącą
   * kod źródłowy i logikę biznesową przed nieautoryzowanym transferem.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku sukcesu).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/partner-products-access', {}).pipe(take(1),
      catchError(err => {
        if (err.status === 401) {
          this.router.navigate(['signin']).then(() => {
            this.authService.logout();
          });
        }
        return throwError(err);
      }), map(response => {
        console.log('check - partner-products - ok');
        return true;
      }));
  }
}
